<template>
  <div class="app-container">
    <div class="chatbot">
      <div class="chat-wrapper">
        <div class="button-row">
          <!-- 戻るボタン -->
          <button v-if="showBackButton" @click="goBack" class="back-button" :aria-label="backButtonText">
            <p class="back-button-arrow">←</p>
            <p v-html="backButtonText"></p>
          </button>
          <div v-else class="back-button-spacer"></div>

          <div v-if="title" class="title-container" @click="toggleInfo">
            <h2 :style="{ color: botFontColor }">{{ title }}</h2>
            <span v-if="showInfoButton" class="triangle-icon" :class="{ 'triangle-up': showInfo }">▼</span>
          </div>
          <div v-if="showInfo && infoContent" class="info-panel">
            <div v-html="infoContent"></div>
          </div>

          <!-- リロードボタン -->
          <button v-if="showReloadButton" @click="reloadChat" class="reload-button" aria-label="チャットをリロード">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </button>
          <div v-else class="reload-button-spacer"></div>
        </div>

        <div class="messages" ref="messageContainer" :class="{ 'has-background-image': backgroundType === 'image' }"
          :style="getBackgroundStyle()">
          <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.sender]">
            <!-- メッセージの吹き出し部分 -->
            <div :style="{
              '--font-color': msg.sender === 'user' ? userFontColor : botFontColor,
              '--background-color': msg.sender === 'user' ? userFontBackgroundColor : botFontBackgroundColor,
              '--border-color': msg.sender === 'user' ? userFontBorderColor : botFontBorderColor
            }" class="bubble">
              <div v-if="msg.count" class="count-container">
                <p>{{ msg.count }}</p>
              </div>
              <div class="message-text-container" :class="{ 'message-with-count': msg.count }">
                <p v-html="formatMessage(msg.text)"></p>
              </div>
              <!-- アクションボタン領域（吹き出しの中に表示する場合） -->
              <div
                v-if="msg.actionPosition === 'inside' && msg.actions && msg.actions.length > 0 && msg.actionsVisible !== false"
                class="actions-inside">
                <button v-for="(action, actionIndex) in msg.actions" :key="actionIndex"
                  class="action-button inside-button" @click="handleActionClick(action)" :disabled="isTyping">
                  {{ action.label }}
                </button>
              </div>

              <div v-if="msg.questionRestNumber" class="question-rest-number-container">
                <p>{{ msg.questionRestNumber }}</p>
              </div>
            </div>

            <!-- アクションボタン領域（吹き出しの外に表示する場合） -->
            <div
              v-if="(!msg.actionPosition || msg.actionPosition === 'outside') && msg.actions && msg.actions.length > 0 && msg.actionsVisible !== false"
              class="actions-container">
              <button v-for="(action, actionIndex) in msg.actions" :key="actionIndex" class="action-button"
                @click="handleActionClick(action)" :disabled="isTyping">
                {{ action.label }}
              </button>
            </div>
          </div>
          <div v-if="isTyping" class="message bot">
            <div class="bubble typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
        <div class="input-container" v-if="showInputContainer">
          <textarea class="user-input-textarea" v-model="userInput" @compositionstart="isComposing = true"
            @compositionend="isComposing = false" @keydown.enter="handleEnterKey" :placeholder="inputPlaceholder"
            :disabled="isTyping" rows="1" ref="textarea"></textarea>
          <button @click="handleClickSendMessage" class="send-button"
            :disabled="isTyping || !userInput.trim()">
            <span>{{ displaySendButtonText }}</span>
          </button>
        </div>
        <slot name="logo"></slot>
      </div>

      <!-- スロット：補助コンポーネント -->
      <div class="auxiliary-component chat-wrapper">
        <slot name="auxiliary"></slot>
      </div>
    </div>

    <!-- ログインダイアログ -->
    <login-dialog v-if="requireLogin" />

    <!-- 課金ダイアログ -->
    <yagiprime-payment-dialog v-if="requirePayment" />
  </div>
</template>

<script>
import LoginDialog from '../../../components/LoginDialog.vue'
import YagiprimePaymentDialog from '../../../components/yagiprime/YagiprimePaymentDialog.vue'

export default {
  name: 'ChatbotComponent',
  components: {
    LoginDialog,
    YagiprimePaymentDialog
  },
  props: {
    // 基本設定
    title: {
      type: String,
      default: 'チャットボット'
    },
    backButtonText: {
      type: String,
      default: '戻る'
    },
    backRoute: {
      type: String,
      default: '/'
    },
    inputPlaceholder: {
      type: String,
      default: 'メッセージを入力...'
    },
    sendButtonText: {
      type: String,
      default: '送信'
    },

    // 初期メッセージ設定
    initialMessages: {
      type: Array,
      default: () => []
    },
    greetingMessage: {
      type: String,
      default: 'こんにちは！お手伝いします。'
    },
    firstPrompt: {
      type: String,
      default: ''
    },

    // API設定
    useDefaultApi: {
      type: Boolean,
      default: true
    },
    apiEndpoint: {
      type: String,
      default: ''
    },
    apiParams: {
      type: Object,
      default: () => ({})
    },

    // UI表示設定
    showBackButton: {
      type: Boolean,
      default: true
    },
    showReloadButton: {
      type: Boolean,
      default: true
    },
    showInfoButton: {
      type: Boolean,
      default: false
    },
    infoContent: {
      type: String,
      default: ''
    },
    showInputContainer: {
      type: Boolean,
      default: true
    },
    userFontColor: {
      type: String,
      default: '#fff'
    },
    userFontBackgroundColor: {
      type: String,
      default: '#0F9934'
    },
    userFontBorderColor: {
      type: String,
      default: '#0F9934'
    },
    botFontColor: {
      type: String,
      default: '#000'
    },
    botFontBackgroundColor: {
      type: String,
      default: '#EFF5E9'
    },
    botFontBorderColor: {
      type: String,
      default: '#EFF5E9 '
    },
    backgroundTheme: {
      type: String,
      default: '#fff'
    },
    backgroundImage: {
      type: String,
      default: ''
    },
    backgroundType: {
      type: String,
      default: 'color', // 'color' or 'image'
      validator: (value) => ['color', 'image'].includes(value)
    },

    // 課金・認証設定
    requireLogin: {
      type: Boolean,
      default: false
    },
    requirePayment: {
      type: Boolean,
      default: false
    },
    paymentFeature: {
      type: String,
      default: ''
    },
    paymentTiming: {
      type: Number,
      default: 0 // 0は課金なし、1以上はn回目のメッセージから課金
    },

    // ログ設定
    enableLogging: {
      type: Boolean,
      default: true
    },
    logDetail: {
      type: String,
      default: 'normal' // 'minimal', 'normal', 'detailed'
    }
  },
  data() {
    return {
      userInput: '',
      messages: [],
      isComposing: false,
      isTyping: false,
      showInfo: false,
      hasInteracted: false,
      messageCount: 0,
      logs: [], // チャットログを保存する配列
      sessionStartTime: new Date().toISOString(),
      isButtonClick: false
    }
  },
  computed: {
    canSendMessage() {
      // 課金条件によって送信可能かどうかを判定
      if (this.paymentTiming === 0) return true
      if (!this.hasInteracted) return true

      // messageCountがpaymentTimingと一致する場合のみ課金
      return this.messageCount >= this.paymentTiming ? (this.$globalVar.isYagiPrime || false) : true
    },
    displaySendButtonText() {
      // 次のメッセージが課金対象の場合は🔒️アイコンを表示
      if (!this.$globalVar.isYagiPrime && (this.requirePayment && this.messageCount >= this.paymentTiming - 1 && this.hasInteracted)) {
        return '🔒️'
      }
      return this.sendButtonText
    },
    backgroundStyle() {
      if (this.backgroundType === 'color') {
        return { backgroundColor: this.backgroundTheme }
      } else if (this.backgroundType === 'image') {
        return { '--background-image': `url(${this.backgroundImage})` }
      }
      return {}
    }
  },
  created() {
    // 初期メッセージの設定
    this.messages = this.initialMessages.map(msg => ({
      sender: msg.role === 'assistant' ? 'bot' : 'user',
      text: msg.content
    }))

    // 課金サービスの初期化
    if (this.requirePayment) {
      this.$yagiprimePaymentService.init(this)
    }

    // セッション開始ログの記録
    this.logEvent('session_start', { initialMessagesCount: this.initialMessages.length })
  },
  watch: {
    userInput() {
      this.$nextTick(this.adjustTextareaHeight)
    }
  },
  mounted() {
    window.addEventListener('resize', this.adjustMessagesHeight)
    this.adjustMessagesHeight()

    setTimeout(() => {
      if (this.messages.length === 0 && this.greetingMessage) {
        this.showGreetingWithDelay()
      }
    }, 500)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustMessagesHeight)
    // セッション終了ログを記録
    this.logSessionEnd()
  },
  destroyed() {
    if (this.requirePayment) {
      this.$yagiprimePaymentService.cleanup(this)
    }
  },
  updated() {
    this.scrollToBottom()
    if (this.logDetail === 'detailed') {
      this.logEvent('component_updated')
    }
  },
  methods: {
    toggleInfo() {
      if (this.showInfoButton) {
        this.showInfo = !this.showInfo
        this.logEvent('info_toggled', { currentVisibility: this.showInfo })
      }
    },
    handleEnterKey(e) {
      if (e.keyCode === 13 && !e.shiftKey && !this.isComposing) {
        e.preventDefault()
        if (this.userInput.trim() !== '' && !this.isTyping) {
          this.logEvent('send_via_enter_key')
          this.handleClickSendMessage()
        }
      }
    },
    handleClickSendMessage() {
      this.logEvent('send_button_clicked')
      // 課金とログインが必要かつタイミングが合致する場合
      const app = this

      // ログインが必要な場合の処理を追加
      if (this.requireLogin && !this.$firebase.isAuthenticated) {
        this.logEvent('login_required', { feature: this.paymentFeature })
        this.$yagiprimePaymentService.showLoginDialog(this)
        return
      }

      if (this.requirePayment && this.shouldCheckPayment()) {
        this.logEvent('payment_required', { feature: this.paymentFeature })
        this.$yagiprimePaymentService.useFeature(this, this.paymentFeature, {
          onSuccess: () => {
            app.logEvent('payment_success', { feature: app.paymentFeature })
            app.sendMessage()
          },
          onCancel: () => {
            app.logEvent('payment_canceled', { feature: app.paymentFeature })
          }
        })
      } else {
        this.sendMessage()
      }
    },
    shouldCheckPayment() {
      this.logEvent('payment_check_logic_executed', {
        paymentTiming: this.paymentTiming,
        hasInteracted: this.hasInteracted,
        messageCount: this.messageCount,
        result: this.messageCount >= this.paymentTiming - 1 && this.hasInteracted && this.paymentTiming !== 0
      })
      // 課金またはログイン規制なしの場合は常にfalse
      if (this.paymentTiming === 0) return false
      // まだ一度も会話してない場合はチェック不要
      if (!this.hasInteracted) return false
      // 鍵マークが表示される条件と一致させる
      return this.messageCount >= this.paymentTiming - 1
    },
    async sendMessage () {
      if (this.userInput.trim() === '') return

      this.logEvent('send_message_process_started')

      // ユーザーメッセージを追加
      const userMessageText = this.userInput.trim()
      // ボタンクリック経由でない場合のみメッセージを追加
      // ボタンクリックの場合は handleActionClick で既に追加されている or すぐに追加される
      if (!this.isButtonClick) {
        this.messages.push({ sender: 'user', text: userMessageText })
      }
      this.isButtonClick = false // フラグをリセット

      const userMessage = this.userInput // API送信のため保持
      this.userInput = ''
      this.isTyping = true
      this.hasInteracted = true
      this.messageCount++

      // メッセージ送信ログ
      this.logEvent('message_sent', {
        messageCount: this.messageCount,
        messageLength: userMessageText.length // 送信テキスト長
      })

      // APIメッセージ形式に変換 (actionsを含むようにする）
      const apiMessages = this.messages
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text + (msg.actions ? `actions:${JSON.stringify(msg.actions)}` : '')
        }))

      // APIに送信するパラメータを準備
      const params = {
        prompt: apiMessages, // ボタンを含まない履歴を送信
        ...this.apiParams
      }

      // ユーザーIDがあれば追加
      if (this.$firebase && this.$firebase.user) {
        params.uuid = this.$firebase.user.uid
      }

      if (!this.useDefaultApi) {
        this.logEvent('send_message_process_started')
        this.$emit('message-sent', {
          userMessage: userMessageText,
          botReply: 'APIが呼び出されました。',
          messageCount: this.messageCount,
          params: params
        })
        return
      }

      try {
        // API呼び出しの開始をログ
        this.logEvent('api_call_start', { endpoint: this.apiEndpoint })
        const startTime = new Date().getTime()

        // API呼び出し
        const response = await this.$axios.post(this.apiEndpoint, params)
        // API呼び出しの完了をログ
        const endTime = new Date().getTime()
        this.logEvent('api_call_end', {
          duration: endTime - startTime,
          status: response.status,
          hasReply: !!response.data.reply,
          hasActions: !!(response.data.actions && response.data.actions.length > 0)
        })

        // 応答があれば表示
        if (response.data.reply) {
          // API応答に actions や actionPosition が含まれていれば、それもメッセージに追加
          const botMessage = {
            sender: 'bot',
            text: response.data.reply,
            actions: response.data.actions || [], // actions がなければ空配列
            actionPosition: response.data.actionPosition, // APIからの位置指定（なければundefined）
            actionsVisible: true // アクションの表示フラグを追加
          }
          await this.typeMessage(botMessage)
          this.logEvent('reply_received', {
            replyLength: response.data.reply.length,
            actionsCount: botMessage.actions.length
          })
        }

        // メッセージが送信されたことを通知
        this.$emit('message-sent', {
          userMessage,
          botReply: response.data.reply,
          messageCount: this.messageCount
        })
      } catch (error) {
        console.error('メッセージの送信中にエラーが発生しました:', error)
        this.logEvent('error', {
          type: 'api_error',
          message: error.message,
          code: error.response ? error.response.status : null
        })
        // エラーメッセージにも action はつけない
        await this.typeMessage({ sender: 'bot', text: 'エラーが発生しました。しばらく経ってからもう一度お試しください。' })
        this.$emit('error', error)
      }

      this.isTyping = false
    },
    goBack() {
      this.logEvent('back_button_clicked', { targetRoute: this.backRoute })
      this.$router.push(this.backRoute)
    },
    reloadChat() {
      this.logEvent('chat_reloaded_start')
      this.messages = []
      this.userInput = ''
      this.hasInteracted = false
      this.messageCount = 0
      this.showGreetingWithDelay()
      this.$emit('chat-reloaded')
      this.logEvent('chat_reloaded_end')
    },
    showGreetingWithDelay() {
      if (!this.greetingMessage) {
        this.isTyping = false
        return
      }

      this.logEvent('greeting_display_started')
      this.isTyping = true
      setTimeout(async () => {
        await this.typeMessage('bot', this.greetingMessage)
        // greetingMessage 表示完了後にイベントを発行
        this.$emit('greeting-displayed')
        this.logEvent('greeting_display_finished') // ログも追加

        if (this.firstPrompt) {
          this.isTyping = true
          setTimeout(() => {
            this.typeMessage('bot', this.firstPrompt)
          }, 1000)
        }
      }, 1000)
    },
    async typeMessage(sender, text) {
      // 引数をオブジェクトに変更して actions と count を受け取れるようにする
      let messageObject
      if (typeof sender === 'object') {
        messageObject = sender // sender がオブジェクトの場合、そのまま使う
      } else {
        // 既存の呼び出し方との互換性のため
        messageObject = { sender, text, actions: [] }
      }

      // messages 配列に追加するオブジェクトを作成
      const messageToAdd = {
        sender: messageObject.sender,
        text: messageObject.text,
        actions: messageObject.actions || [], // actions がなければ空配列
        actionPosition: messageObject.actionPosition, // actionPosition も追加
        actionsVisible: messageObject.actionsVisible !== undefined ? messageObject.actionsVisible : true // 表示フラグを追加
      }
      // count プロパティが存在し、null や undefined でなければ追加
      if (messageObject.count !== undefined && messageObject.count !== null) {
        messageToAdd.count = messageObject.count
      }
      // questionRestNumber プロパティが存在し、null や undefined でなければ追加
      if (messageObject.questionRestNumber !== undefined && messageObject.questionRestNumber !== null) {
        messageToAdd.questionRestNumber = messageObject.questionRestNumber
      }

      // ログに actionPosition を追加
      this.logEvent('typing_message_started', {
        sender: messageToAdd.sender,
        textLength: messageToAdd.text.length,
        actionsCount: messageToAdd.actions.length || 0,
        count: messageToAdd.count,
        actionPosition: messageToAdd.actionPosition, // ログに追加
        actionsVisible: messageToAdd.actionsVisible // 表示フラグもログに追加
      })

      this.messages.push(messageToAdd) // 準備したオブジェクトを messages 配列に追加

      this.$nextTick(() => {
        this.scrollToBottom()

        // botからのメッセージが表示された後、テキストボックスにフォーカス
        if (messageToAdd.sender === 'bot' && this.$refs.textarea && !this.isTyping) {
          // モバイルでのスクロール後のフォーカスを安定させるため少し遅延させる
          setTimeout(() => {
            this.$refs.textarea.focus()
            this.logEvent('textarea_focused_after_bot_message')
          }, 100)
        }
      })

      if (messageToAdd.sender === 'bot') {
        this.isTyping = false
      }
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    adjustMessagesHeight() {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    },
    formatMessage(text) {
      return text.replace(/\n/g, '<br>')
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = textarea.scrollHeight + 'px'
        if (this.logDetail === 'detailed') {
          this.logEvent('textarea_height_adjusted', { newHeight: textarea.style.height })
        }
      }
    },

    // ログ関連メソッド
    logEvent(eventType, data = {}) {
      if (!this.enableLogging) return

      const logEntry = {
        timestamp: new Date().toISOString(),
        eventType,
        sessionId: this.sessionStartTime,
        ...data
      }

      // ユーザーIDがあれば追加
      if (this.$firebase && this.$firebase.user) {
        logEntry.userId = this.$firebase.user.uid
      }

      // プロパティに応じてログの詳細度を調整
      if (this.logDetail === 'detailed') {
        logEntry.chatState = {
          messageCount: this.messageCount,
          messagesLength: this.messages.length
        }
      } else if (this.logDetail === 'minimal') {
        // minimal の場合のログ内容を制限するなど、必要に応じて調整
        const minimalEvents = ['session_start', 'session_end', 'message_sent', 'reply_received', 'api_call_start', 'api_call_end', 'error', 'payment_required', 'payment_success', 'payment_canceled', 'chat_reloaded']
        if (!minimalEvents.includes(eventType)) {
          return // minimalレベルでは記録しないイベントタイプはここで弾く
        }
      }

      // ログをローカル配列に追加
      this.logs.push(logEntry)

      // ログイベントを発火
      this.$emit('log-event', logEntry)

      // コンソールにログを出力（開発用）
      if (process.env.NODE_ENV === 'development') {
        // minimalの場合、コンソール出力も制限するなど調整可能
        if (this.logDetail !== 'minimal' || (logEntry.userId || this.logs.length < 100)) { // minimalでもユーザーIDがあるか、ログが少ないうちは表示する例
          console.log(`[ChatLog][${eventType}]`, logEntry)
        }
      }
    },

    // ログをエクスポート
    exportLogs() {
      return JSON.stringify(this.logs)
    },

    // ログをクリア
    clearLogs() {
      this.logs = []
      this.logEvent('logs_cleared')
    },

    // セッション終了時のログ
    logSessionEnd() {
      const sessionDuration = new Date().getTime() - new Date(this.sessionStartTime).getTime()
      this.logEvent('session_end', {
        sessionDuration,
        totalMessages: this.messageCount,
        messagesCount: this.messages.length
      })
    },

    // 親コンポーネントからボットメッセージを表示するための新しいメソッド
    // actionPosition 引数を追加 (デフォルトは 'outside')
    async showBotMessageFromParent(messageText, actions = [], count = null, questionRestNumber = null, actionPosition = 'outside') {
      if (!messageText) return // メッセージが空なら何もしない

      // actionsがnullの場合は空配列に変換
      if (actions === null) {
        actions = []
      }

      this.logEvent('show_bot_message_from_parent_called', {
        textLength: messageText ? messageText.length : 0,
        actionsCount: actions && Array.isArray(actions) ? actions.length : 0,
        count: count,
        questionRestNumber: questionRestNumber,
        actionPosition: actionPosition // ログに actionPosition を追加
      })
      this.isTyping = true // タイピングインジケーターを表示

      // 少し遅延させてタイピング表示を見せる（任意）
      await new Promise(resolve => setTimeout(resolve, 500))

      // botMessage オブジェクトに actionPosition を含める
      const botMessage = {
        sender: 'bot',
        text: messageText,
        actions: actions,
        count: count,
        questionRestNumber: questionRestNumber,
        actionPosition: actionPosition, // 親から渡された actionPosition を含める
        actionsVisible: true // 追加
      }
      await this.typeMessage(botMessage)

      // typeMessage内で isTyping = false になるはずだが、念のため確認
      // this.isTyping = false // typeMessageが適切に処理するなら不要
    },

    handleActionClick (action) {
      const value = action.value
      const url = action.url
      this.logEvent('action_button_clicked', { value })

      // ログインが必要な場合の処理を追加
      if (this.requireLogin && !this.$firebase.isAuthenticated) {
        this.logEvent('login_required_for_action', { value })
        this.$yagiprimePaymentService.showLoginDialog(this)
        return
      }

      // クリックされたボタンの値をユーザーメッセージとして送信準備
      this.userInput = value
      this.isButtonClick = true // ボタンクリックからの送信であることを示すフラグ
      // ユーザーメッセージとして表示
      this.messages.push({ sender: 'user', text: value })

      if (url) {
        this.$router.push(url)
      } else {
        // 送信処理を呼び出す
        this.handleClickSendMessage() // 既存の送信ボタンクリック処理を流用
        // ボタンを含むメッセージのactionsを空にする (再クリック防止)
        const lastBotMessage = this.messages.slice().reverse().find(m => m.sender === 'bot' && m.actions && m.actions.length > 0)
        if (lastBotMessage) {
          lastBotMessage.actionsVisible = false // actionsは保持して、非表示にする
        }
      }
    },
    getBackgroundStyle() {
      if (this.backgroundType === 'color') {
        return {
          backgroundColor: this.backgroundTheme
        }
      } else if (this.backgroundType === 'image') {
        return {
          '--background-image': `url(${this.backgroundImage})`
        }
      }
      return {}
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.chat-wrapper {
  width: 100%;
  max-width: 768px;
  margin: 0 auto 10px;
}

.chatbot {
  width: 100%;
  position: relative;
  background: #FFFFE8;
  padding: 10px 0;
}

.messages {
  height: calc(100vh - 340px);
  overflow-y: scroll;
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 5px;
  scroll-behavior: smooth;
  position: relative;
}

.messages.has-background-image {
  background-image:
      linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)),
      var(--background-image);
  background-size: cover;
  background-attachment: fixed;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.messages.has-background-image::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.3);
  z-index: -1;
  pointer-events: none;
}

.message {
  position: relative;
  z-index: 2;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.bubble {
  max-width: 80%;
  border-radius: 18px;
  position: relative;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.4;
}

.message-text-container {
  padding: 10px 15px;
}

.question-rest-number-container {
  /* 上ボーダーに白い線を追加 */
  border-top: 1px solid #fff;
  padding: 10px 15px;
  text-align: center;
}

.count-container {
  position: absolute;
  /* bubble の内側で絶対配置 */
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 11px;
  font-size: 15px;
  background-color: #014a5c;
  color: #fff;
  font-weight: bold;
  border-radius: 0px 18px 0px 18px;
}

.message-with-count {
  font-weight: bold;
  padding: 25px 50px 25px 20px;
  font-size: 16px;
}

.user {
  align-items: flex-end;
}

.user .bubble {
  background-color: var(--background-color);
  color: var(--font-color);
  border: 1px solid var(--border-color);
  border-bottom-right-radius: 4px;
}

.bot {
  align-items: flex-start;
}

.bot .bubble {
  color: var(--font-color);
  background: var(--background-color);
  border-bottom-left-radius: 4px;
}

.input-container {
  display: flex;
  margin-top: 10px;
  align-items: flex-end;
}

.user-input-textarea {
  flex-grow: 1;
  padding: 10px;
  background-color: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 20px;
  color: #606266;
  font-size: 16px;
  min-height: 40px;
  max-height: 120px;
  resize: none;
  overflow-y: auto;
  box-sizing: border-box;
  line-height: 1.4;
  display: block;
  margin-right: 10px;
}

.send-button {
  padding: 10px 20px;
  background-color: #0F9934;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: inherit;
  transition: background-color 0.3s;
  max-height: 40px;
}

.send-button:hover {
  background-color: #0F9934;
}

.send-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
}

.back-button,
.reload-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.3s ease;
  margin: 0;
  z-index: 2;
}

.reload-button:hover {
  transform: rotate(360deg);
}

.back-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.3s ease;
  margin: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 5px;
  text-align: left;
  font-size: 12px;
  opacity: 0.4;
}

.back-button p {
  margin: 0;
  line-height: 1;
  text-align: left;
}

.back-button:hover {
  transform: scale(1.1);
}

.back-button-arrow {
  font-weight: bold;
  font-size: 20px;
}

.title-container {
  position: absolute;
  left: 48%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
}

.info-panel {
  position: fixed;
  top: 13%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px 15px;
  margin-top: 5px;
  font-size: 14px;
  text-align: center;
  color: #555;
  z-index: 10;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  max-width: 400px;
}

.triangle-icon {
  font-size: 12px;
  margin-left: 5px;
  transition: transform 0.3s ease;
}

.triangle-up {
  transform: rotate(180deg);
}

.back-button-spacer,
.reload-button-spacer {
  width: 24px;
  height: 24px;
  z-index: 2;
}

.typing-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 15px;
}

.typing-indicator span {
  height: 10px;
  width: 10px;
  float: left;
  margin: 0 1px;
  background-color: #9E9EA1;
  display: block;
  border-radius: 50%;
  opacity: 0.4;
}

.typing-indicator span:nth-of-type(1) {
  animation: 1s blink infinite 0.3333s;
}

.typing-indicator span:nth-of-type(2) {
  animation: 1s blink infinite 0.6666s;
}

.typing-indicator span:nth-of-type(3) {
  animation: 1s blink infinite 0.9999s;
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .chat-wrapper {
    width: 100%;
    padding: 0 10px;
  }

  .messages {
    height: calc(100vh - 250px);
  }

  h2 {
    font-size: 20px;
  }

  .bubble {
    font-size: 13px;
  }

  .user-input-textarea,
  .send-button {
    font-size: 14px;
  }
}

/* --- 新しいスタイル --- */
.actions-container {
  margin-top: 10px;
  margin-bottom: 15px;
  display: flex;
  flex-wrap: wrap;
  /* ボタンが複数行になっても良いように */
  gap: 8px;
  /* ボタン間の隙間 */
  justify-content: flex-start;
  /* ボタンを左寄せ（botメッセージの場合） */
}

.user .actions-container {
  justify-content: flex-end;
  /* ユーザーメッセージの場合は右寄せ */
}

.action-button {
  padding: 8px 15px;
  background-color: #fff;
  /* 白背景 */
  color: #0F9934;
  /* 青文字 */
  border: 1px solid #0F9934;
  /* 青い境界線 */
  border-radius: 15px;
  /* 角丸 */
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
  /* ボタンテキストが折り返さないように */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  /* わずかな影を追加 */
}

.action-button:hover:not(:disabled) {
  background-color: #e6f3ff;
  /* ホバー時に薄い青背景 */
}

.action-button:disabled {
  background-color: #f0f0f0;
  border-color: #ccc;
  color: #aaa;
  cursor: not-allowed;
}

/* 吹き出し内ボタン用のスタイル調整 */
.actions-inside {
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  flex-direction: column;
  padding: 10px 15px;
}

.inside-button {
  background-color: #014a5c;
  color: #fff;
  border-color: #014a5c;
  font-weight: bold;
  font-size: 15px;
  border-radius: 7px;
}

.action-button:hover:not(:disabled) {
  background-color: #014a5c;
  color: #fff;
  border-color: #014a5c;
}

.user .actions-inside {
  /* ユーザーメッセージ内のボタンも左寄せのままにするか、必要なら調整 */
  justify-content: flex-start;
}
</style>
